import { memo, useMemo, useRef, useState } from 'react'
import { MediumTinyBold, Tiny2 } from '@/components/atoms/typography'
import { transparentize } from 'polished'
import { v4 as uuidv4 } from 'uuid'
import { Flex } from '../atoms/Grid'
import { ArrowDownIconDefault } from '../atoms/icons/ArrowDownIconDefault'
import { ArrowUpIconDefault } from '../atoms/icons/ArrowUpIconDefault'
import SearchInput from '../atoms/searchInput'
import Spinner from './spinner'
import { useEventListenerHook } from 'hooks/useEventListener'
import styled, { css } from 'styled-components'

type SelectOptionsProps<T> = {
  options: T[]
  loading?: boolean
  handleSelect?: (item: T) => void
  selectedItem?: T
  placeholder?: string
  isError?: boolean
  adjustPlaceholderPosition?: boolean
  errorPlaceholder?: string
}

export const SelectOptions = memo(
  <T,>({
    options = [],
    loading = false,
    handleSelect,
    selectedItem,
    placeholder = '',
    isError = false,
    adjustPlaceholderPosition,
    errorPlaceholder
  }: SelectOptionsProps<
    T & { id?: string | number; name?: string; value?: string }
  >) => {
    const [openDropdown, setOpenDropdown] = useState<boolean>(false)
    const keysIds = options.map(() => useMemo(() => uuidv4(), []))

    const selectDropDownRef = useRef(null)
    useEventListenerHook({
      eventName: 'click',
      handler: (event) => {
        if (
          selectDropDownRef.current &&
          !selectDropDownRef.current.contains(event.target)
        ) {
          setOpenDropdown(false)
        }
      }
    })

    return (
      <Wrapper
        width={{ desktop: '100%', tablet: 'auto', mobile: 'auto' }}
        ref={selectDropDownRef}
      >
        <Flex width="100%" position="relative">
          <StyledInput
            value={selectedItem?.value ?? ''}
            onClick={() => setOpenDropdown(!openDropdown)}
            isError={isError}
            readOnly
          />
          <div>{openDropdown ? <IconUp /> : <IconDown />}</div>
          <>
            <LabelWrapper adjustPlaceholderPosition={adjustPlaceholderPosition}>
              <StyledTextLabel
                movePlaceholder={openDropdown || selectedItem?.value}
              >
                {placeholder}
              </StyledTextLabel>
            </LabelWrapper>
            <ErrorWrapper adjustPlaceholderPosition={adjustPlaceholderPosition}>
              {isError && (
                <StyledMediumTinyBold>{errorPlaceholder}</StyledMediumTinyBold>
              )}
            </ErrorWrapper>
          </>
        </Flex>
        <StyledDropdown
          display={
            openDropdown && (options.length > 0 || loading) ? 'flex' : 'none'
          }
          minHeight={70}
        >
          <DropdownContent>
            {options.map((option, idx) => (
              <StyledOption
                key={keysIds[idx]}
                onClick={(e) => {
                  e.preventDefault()
                  handleSelect(option)
                  setOpenDropdown(false)
                }}
                isSelected={
                  selectedItem?.id === option?.id ||
                  selectedItem?.value === option?.value
                }
              >
                {option?.value}
              </StyledOption>
            ))}
          </DropdownContent>
          {loading && (
            <StyledFlex>
              <StyledSpinner />
            </StyledFlex>
          )}
        </StyledDropdown>
      </Wrapper>
    )
  }
)

const StyledOption = styled(Tiny2)`
  margin: 0;
  padding: 10px;
  font-weight: normal;
  background-color: ${({ isSelected, theme }) =>
    isSelected
      ? transparentize(0.9, theme.colors.onBackground)
      : 'transparent'};
  &:hover {
    cursor: pointer;
    background-color: ${({ isSelected, theme }) =>
      isSelected
        ? transparentize(0.9, theme.colors.onBackground)
        : transparentize(0.95, theme.colors.onBackground)};
  }
`

const StyledDropdown = styled(Flex)`
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  -o-box-sizing: border-box;
  -ms-box-sizing: border-box;
  box-sizing: border-box;
  flex-direction: column;
  position: absolute;
  z-index: 100;
  max-height: 200px;
  width: 100%;
  top: 100%;
  left: 0;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.onBackground};
  border-top: none;
  border-radius: ${(props) => props.theme.borderRadius}px;
`

const DropdownContent = styled(Flex)`
  flex-direction: column;
  overflow-y: auto;
  -ms-scroll-chaining: none;
  overscroll-behavior: auto;
`

const IconDown = styled(ArrowDownIconDefault)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  margin-top: 2px;
`

const IconUp = styled(ArrowUpIconDefault)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  margin-top: 2px;
`

const Wrapper = styled(Flex)`
  flex-direction: column;
  position: relative;
`
const InputBase = css`
  border: 1px solid
    ${(props) =>
      props.isError
        ? props.theme.colors.errorSecondary
        : props.theme.colors.onBackground};
  border-radius: ${(props) => props.theme.borderRadius}px;
  flex-grow: 1;
  margin: 0;
  &::placeholder {
    color: ${(props) => props.theme.colors.inputPlaceholder};
  }
  &::-ms-input-placeholder {
    color: ${(props) => props.theme.colors.inputPlaceholder};
  }
  &:focus {
    outline-style: none;
    border: 1px solid
      ${(props) =>
        props.isError
          ? props.theme.colors.errorSecondary
          : props.theme.colors.onBackground};
  }
`
const StyledInput = styled(SearchInput)`
  ${InputBase};
  width: ${(props) => props.width || 'auto'};
  height: 60px;
  padding: ${(props) => props.p ?? props.padding ?? '0 13px'};
  font-size: 16px;
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  flex-grow: 1;
  &:-webkit-autofill,
  &:autofill {
    + div label {
      font-weight: 500;
      transition: all 0.125s ease-out;
      color: ${({ theme }) => theme.colors.inputPlaceholder};
      margin-top: 4px;
      font-size: 12px;
    }
  }
`

const StyledSpinner = styled(Spinner)``

const StyledFlex = styled(Flex)`
  position: absolute;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
`
const LabelWrapper = styled(Flex)`
  position: absolute;
  pointer-events: none;
  left: ${(props) => !props.adjustPlaceholderPosition && '13px'};
  padding-left: ${(props) => props.adjustPlaceholderPosition && '13px'};
`
const StyledTextLabel = styled.label`
  font-weight: 500;
  transition: all 0.125s ease-out;
  color: ${({ theme }) => theme.colors.inputPlaceholder};
  margin-top: ${(props) => (props.movePlaceholder ? '4px' : '20px')};
  font-size: ${(props) => (props.movePlaceholder ? '12px' : '16px')};
`
const ErrorWrapper = styled(Flex)`
  position: absolute;
  left: ${(props) => !props.adjustPlaceholderPosition && '13px'};
  padding-left: ${(props) => props.adjustPlaceholderPosition && '13px'};
  margin-top: 46px;
`
const StyledMediumTinyBold = styled(MediumTinyBold)`
  min-height: 13px;
  width: 100%;
  color: ${({ theme }) => theme.colors.errorSecondary};
  margin: 0;
`
